import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  Bookmark,
  Trash2,
  MapPin,
  Clock,
  Loader2,
  ArrowRight,
  ArrowLeft,
  Share2,
  FileDown,
  Star,
  CalendarDays,
  Sparkles,
} from "lucide-react";
import { useAuth } from "@/hooks/use-auth";
import { SavedPlacesSection } from "@/components/SavedPlacesSection";

import { useLang } from "@/hooks/use-lang";
import { getConciergeDisplayName } from "@/lib/i18n";
import { buildProShareUrl } from "@/lib/share-url";
import { getItinerary, queueLoadItinerary, type SavedItinerary } from "@/lib/saved-itineraries";
import { exportSavedItineraryPdf } from "@/lib/saved-pdf-export";
import { getMealDefaults, type MealDefaults } from "@/lib/admin.functions";
import { shareItinerary } from "@/lib/share-link";
import { storePostLoginRedirect } from "@/lib/auth-redirect";
import { listUnifiedRoutes, setRouteFavorite, deleteRoute } from "@/lib/unified-routes.functions";
import { getOriginalsSummary } from "@/lib/originals.functions";
import {
  canDeleteRoute,
  countByFilter,
  filterUnifiedRoutes,
  ORIGIN_LABEL,
  ROUTE_FILTERS,
  type RouteFilter,
  type UnifiedRoute,
} from "@/lib/unified-routes";

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

export const Route = createFileRoute("/my-routes")({
  component: MyRoutesPage,
  head: () => ({
    meta: [
      { title: "As minhas rotas — Routify" },
      {
        name: "description",
        content:
          "Todas as tuas rotas Routify num só sítio: Free, Premium, Pro e planeamentos personalizados.",
      },
      { property: "og:title", content: "As minhas rotas — Routify" },
      {
        property: "og:description",
        content: `Rotas Free, Premium, Pro e ${getConciergeDisplayName("pt")} reunidas em As minhas rotas.`,
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
    ],
  }),
});

const DELETE_TIMEOUT_MS = 15_000;

function getFilterLabel(lang: string | null | undefined): Record<RouteFilter, string> {
  return {
    all: "Todas",
    free: "Free",
    premium: "Premium",
    pro: "Pro",
    concierge: getConciergeDisplayName(lang),
    original: "Originais",
    favorites: "Favoritas",
    recent: "Recentes",
  };
}

const ORIGIN_CLASS: Record<string, string> = {
  free: "border-border text-muted-foreground",
  premium: "border-amber-400/60 text-amber-600",
  pro: "border-brand/60 text-brand",
  concierge: "border-emerald-500/60 text-emerald-600",
  original: "border-sky-500/60 text-sky-600",
};

function withUiTimeout<T>(promise: Promise<T>, ms: number, message: string): Promise<T> {
  return new Promise((resolve, reject) => {
    const timer = window.setTimeout(() => reject(new Error(message)), ms);
    promise
      .then(resolve)
      .catch(reject)
      .finally(() => window.clearTimeout(timer));
  });
}

function MyRoutesPage() {
  const { isAuthenticated, isLoading: authLoading } = useAuth();
  const { lang } = useLang();
  const FILTER_LABEL = useMemo(() => getFilterLabel(lang), [lang]);
  const navigate = useNavigate();
  const [routes, setRoutes] = useState<UnifiedRoute[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<RouteFilter>("all");
  const [mealDefaults, setMealDefaults] = useState<MealDefaults | null>(null);
  const [pdfLoadingId, setPdfLoadingId] = useState<string | null>(null);
  const [openLoadingId, setOpenLoadingId] = useState<string | null>(null);
  const [deletingKey, setDeletingKey] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<UnifiedRoute | null>(null);
  const getMealDefaultsFn = useServerFn(getMealDefaults);
  const listFn = useServerFn(listUnifiedRoutes);
  const favoriteFn = useServerFn(setRouteFavorite);
  const deleteFn = useServerFn(deleteRoute);
  const originalsSummaryFn = useServerFn(getOriginalsSummary);
  const [originalsSummary, setOriginalsSummary] = useState<{
    publishedOriginalCount: number;
    purchasedOriginalCount: number;
    visibleOriginalCount: number;
  } | null>(null);

  const load = useCallback(async () => {
    const [rows, summary] = await Promise.all([
      listFn(),
      originalsSummaryFn().catch(() => null),
    ]);
    setRoutes(rows);
    if (summary) setOriginalsSummary(summary);
  }, [listFn, originalsSummaryFn]);

  useEffect(() => {
    if (authLoading) return;
    if (!isAuthenticated) {
      storePostLoginRedirect();
      navigate({ to: "/login" });
      return;
    }
    let cancelled = false;
    setLoading(true);
    load()
      .catch((e) => {
        if (cancelled) return;
        setRoutes([]);
        toast.error((e as Error).message ?? "Erro a carregar");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [authLoading, isAuthenticated, navigate, load]);

  const counts = useMemo(() => countByFilter(routes ?? []), [routes]);
  const visible = useMemo(
    () => filterUnifiedRoutes(routes ?? [], filter),
    [routes, filter],
  );
  const isEmptyAll = routes !== null && routes.length === 0;
  const showOriginalsCard =
    originalsSummary !== null && originalsSummary.publishedOriginalCount > 0;

  async function handleDelete(route: UnifiedRoute) {
    if (!canDeleteRoute(route)) return;
    setDeletingKey(route.key);
    try {
      await withUiTimeout(
        deleteFn({ data: { source: route.source as "saved" | "pro" | "original", id: route.id } }),
        DELETE_TIMEOUT_MS,
        "A ligação demorou demasiado. Fecha e abre a app, ou entra novamente, e tenta apagar outra vez.",
      );
      setRoutes((cur) => (cur ?? []).filter((r) => r.key !== route.key));
      toast.success(
        route.source === "original"
          ? "Rota removida da lista. Continua tua — podes repô-la na página da rota."
          : "Rota apagada",
      );
    } catch (e) {
      toast.error((e as Error).message ?? "Erro a apagar");
    } finally {
      setDeletingKey(null);
      setConfirmDelete(null);
    }
  }

  async function toggleFavorite(route: UnifiedRoute) {
    if (!route.canFavorite) return;
    const next = !route.favorite;
    setRoutes((cur) =>
      (cur ?? []).map((r) => (r.key === route.key ? { ...r, favorite: next } : r)),
    );
    try {
      await favoriteFn({
        data: { source: route.source as "saved" | "pro", id: route.id, favorite: next },
      });
    } catch (e) {
      setRoutes((cur) =>
        (cur ?? []).map((r) => (r.key === route.key ? { ...r, favorite: !next } : r)),
      );
      toast.error((e as Error).message ?? "Erro a marcar favorita");
    }
  }

  async function handleOpen(route: UnifiedRoute) {
    if (route.source === "original") {
      if (route.shareSlug) {
        navigate({ to: "/originals/$slug", params: { slug: route.shareSlug } });
      } else {
        navigate({ to: "/originals" });
      }
      return;
    }
    if (route.source === "pro") {
      navigate({ to: "/pro", search: { id: route.id } as never });
      return;
    }
    if (route.source === "concierge") {
      navigate({ to: "/concierge/$id", params: { id: route.id } });
      return;
    }
    setOpenLoadingId(route.key);
    try {
      const full = await getItinerary(route.id);
      queueLoadItinerary(full as SavedItinerary);
      navigate({ to: "/" });
    } catch (e) {
      toast.error((e as Error).message ?? "Erro a abrir rota");
    } finally {
      setOpenLoadingId(null);
    }
  }

  async function handleExportPdf(route: UnifiedRoute) {
    if (route.pdfUrl) {
      window.open(route.pdfUrl, "_blank", "noopener");
      return;
    }
    if (route.source !== "saved") {
      // O PDF Pro (com mapas por dia) é gerado na página do roteiro.
      await handleOpen(route);
      return;
    }
    setPdfLoadingId(route.key);
    try {
      const full = await getItinerary(route.id);
      const defaults = mealDefaults ?? (await getMealDefaultsFn().catch(() => null));
      if (defaults && !mealDefaults) setMealDefaults(defaults);
      await exportSavedItineraryPdf(full as SavedItinerary, defaults || undefined);
      toast.success("PDF descarregado");
    } catch (e) {
      toast.error((e as Error).message ?? "Erro a gerar PDF");
    } finally {
      setPdfLoadingId(null);
    }
  }

  async function handleShare(route: UnifiedRoute) {
    if (route.source === "pro") {
      if (!route.shareSlug) {
        toast.info("Abre o roteiro Pro para criar o link de partilha.");
        return;
      }
      await navigator.clipboard
        .writeText(buildProShareUrl(route.shareSlug))
        .then(() => toast.success("Link copiado"))
        .catch(() => toast.error("Não foi possível copiar o link"));
      return;
    }
    if (route.source === "concierge") {
      toast.info("Os planeamentos personalizados são privados.");
      return;
    }
    if (route.source === "original") {
      toast.info("As rotas Routify Originals são pessoais e não têm link de partilha.");
      return;
    }
    const full = await getItinerary(route.id).catch(() => null);
    if (!full) return;
    const slug = await shareItinerary(full as SavedItinerary).catch(() => null);
    if (slug) {
      setRoutes((cur) =>
        (cur ?? []).map((r) => (r.key === route.key ? { ...r, shareSlug: slug } : r)),
      );
    }
  }

  return (
    <main className="mx-auto min-h-[70vh] w-full max-w-3xl px-4 py-8">
      <Link
        to="/"
        className="mb-4 inline-flex items-center gap-1.5 rounded-full border border-border bg-background px-3.5 py-2 text-sm font-semibold text-foreground shadow-sm transition hover:border-brand hover:text-brand"
      >
        <ArrowLeft className="h-4 w-4" />
        Voltar ao início
      </Link>
      <div className="mb-4 flex items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">As minhas rotas</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Todas as tuas rotas — Free, Premium, Pro e planeamentos personalizados.
          </p>
        </div>
        <Link
          to="/"
          className="inline-flex items-center gap-1.5 rounded-full border border-input bg-background px-3 py-1.5 text-sm font-medium hover:bg-accent"
        >
          + Nova rota
        </Link>
      </div>

      <SavedPlacesSection />

      <h2 className="mb-2 text-sm font-semibold tracking-tight text-foreground">Minhas rotas</h2>

      {showOriginalsCard && (
        <Link
          to="/originals"
          className="mb-4 flex items-start gap-3 rounded-2xl border border-border bg-card p-4 shadow-[var(--shadow-soft)] transition hover:border-brand/50"
        >
          <Sparkles className="mt-0.5 h-5 w-5 shrink-0 text-brand" />
          <div className="min-w-0">
            <p className="text-sm font-semibold">Routify Originals</p>
            <p className="mt-0.5 text-sm text-muted-foreground">
              {isEmptyAll
                ? "Ainda não tens rotas guardadas? Começa por uma rota criada pela equipa Routify."
                : originalsSummary !== null &&
                    originalsSummary.purchasedOriginalCount > 0 &&
                    originalsSummary.visibleOriginalCount === 0
                  ? `Tens ${originalsSummary.purchasedOriginalCount} Original(is) comprada(s).`
                  : "Rotas prontas da equipa — itinerários completos, testados e escritos por nós. Compras uma vez e ficam na tua conta."}
            </p>
            <span className="mt-2 inline-flex items-center gap-1 text-sm font-medium text-brand">
              Ver rotas à venda <ArrowRight className="h-4 w-4" />
            </span>
          </div>
        </Link>
      )}

      <div className="mb-5 flex flex-wrap gap-2">
        {ROUTE_FILTERS.map((f) => (
          <button
            key={f}
            type="button"
            onClick={() => setFilter(f)}
            className={`rounded-full border px-3 py-1.5 text-xs font-semibold transition ${
              filter === f
                ? "border-brand bg-brand/10 text-brand"
                : "border-input bg-background text-muted-foreground hover:bg-accent"
            }`}
          >
            {FILTER_LABEL[f]} <span className="opacity-60">{counts[f] ?? 0}</span>
          </button>
        ))}
      </div>

      {loading && (
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" /> A carregar…
        </div>
      )}

      {!loading && visible.length === 0 && !(filter === "all" && showOriginalsCard) && (
        <div className="rounded-2xl border border-dashed border-border bg-background/60 p-8 text-center">
          <Bookmark className="mx-auto h-8 w-8 text-muted-foreground" />
          <p className="mt-3 text-sm text-muted-foreground">
            {filter === "all"
              ? "Ainda não tens rotas guardadas."
              : "Nenhuma rota neste filtro."}
          </p>
          <Link
            to="/"
            className="mt-4 inline-flex items-center gap-1.5 rounded-full bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90"
          >
            Criar a primeira rota <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      )}

      {!loading && visible.length > 0 && (
        <ul className="space-y-3">
          {visible.map((it) => (
            <li
              key={it.key}
              className="rounded-2xl border border-border bg-card p-4 shadow-[var(--shadow-soft)]"
            >
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <h3 className="truncate text-base font-semibold">{it.title}</h3>
                    <span
                      className={`rounded-full border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${
                        ORIGIN_CLASS[it.origin] ?? ORIGIN_CLASS.free
                      }`}
                    >
                      {it.origin === "concierge"
                        ? getConciergeDisplayName(lang)
                        : ORIGIN_LABEL[it.origin]}
                    </span>
                  </div>
                  <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
                    {it.destination && (
                      <span className="inline-flex items-center gap-1">
                        <MapPin className="h-3 w-3" />
                        {it.destination}
                      </span>
                    )}
                    {it.days !== null && (
                      <span className="inline-flex items-center gap-1">
                        <CalendarDays className="h-3 w-3" />
                        {it.days} {it.days === 1 ? "dia" : "dias"}
                      </span>
                    )}
                    {it.stopsCount !== null && (
                      <span className="inline-flex items-center gap-1">
                        <Bookmark className="h-3 w-3" />
                        {it.stopsCount} paragens
                      </span>
                    )}
                    {it.durationHours !== null && (
                      <span className="inline-flex items-center gap-1">
                        <Clock className="h-3 w-3" />
                        {it.durationHours}h
                      </span>
                    )}
                    {it.startDate && (
                      <span className="inline-flex items-center gap-1">
                        <CalendarDays className="h-3 w-3" />
                        {it.startDate}
                        {it.endDate ? ` → ${it.endDate}` : ""}
                      </span>
                    )}
                    <span className="opacity-70">
                      {new Date(it.createdAt).toLocaleDateString()}
                    </span>
                  </div>
                </div>
                <div className="flex items-center gap-1">
                  {it.canFavorite && (
                    <button
                      type="button"
                      onClick={() => toggleFavorite(it)}
                      className={`rounded-full p-2 transition hover:bg-accent ${
                        it.favorite ? "text-amber-500" : "text-muted-foreground"
                      }`}
                      aria-label={it.favorite ? "Remover dos favoritos" : "Marcar favorita"}
                      title={it.favorite ? "Remover dos favoritos" : "Marcar favorita"}
                    >
                      <Star className={`h-4 w-4 ${it.favorite ? "fill-current" : ""}`} />
                    </button>
                  )}
                  {canDeleteRoute(it) && (
                    <button
                      type="button"
                      onClick={() => setConfirmDelete(it)}
                      disabled={deletingKey === it.key}
                      className="rounded-full p-2 text-muted-foreground transition hover:bg-destructive/10 hover:text-destructive disabled:opacity-60"
                      aria-label="Apagar"
                      title="Apagar"
                    >
                      {deletingKey === it.key ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <Trash2 className="h-4 w-4" />
                      )}
                    </button>
                  )}
                </div>
              </div>
              <div className="mt-3 flex flex-wrap justify-end gap-2">
                <button
                  type="button"
                  onClick={() => handleExportPdf(it)}
                  disabled={pdfLoadingId === it.key}
                  className="inline-flex items-center gap-1.5 rounded-full border border-input bg-background px-4 py-1.5 text-sm font-medium hover:bg-accent disabled:opacity-60"
                >
                  {pdfLoadingId === it.key ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <FileDown className="h-4 w-4" />
                  )}
                  PDF
                </button>
                {it.source !== "concierge" && (
                  <button
                    type="button"
                    onClick={() => handleShare(it)}
                    className="inline-flex items-center gap-1.5 rounded-full border border-input bg-background px-4 py-1.5 text-sm font-medium hover:bg-accent"
                  >
                    <Share2 className="h-4 w-4" /> Partilhar
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => handleOpen(it)}
                  disabled={openLoadingId === it.key}
                  className="inline-flex items-center gap-1.5 rounded-full bg-primary px-4 py-1.5 text-sm font-medium text-primary-foreground hover:bg-primary/90"
                >
                  {openLoadingId === it.key ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : null}
                  Abrir rota <ArrowRight className="h-4 w-4" />
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}

      <AlertDialog
        open={confirmDelete !== null}
        onOpenChange={(open) => {
          if (!open && deletingKey === null) setConfirmDelete(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Apagar esta rota?</AlertDialogTitle>
            <AlertDialogDescription>
              {confirmDelete
                ? `“${confirmDelete.title}” (${confirmDelete.origin === "concierge" ? getConciergeDisplayName(lang) : ORIGIN_LABEL[confirmDelete.origin]}) será apagada${
                    confirmDelete.shareSlug ? " e o link de partilha deixa de funcionar" : ""
                  }. Esta ação não pode ser desfeita.`
                : "Esta ação não pode ser desfeita."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deletingKey !== null}>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              disabled={deletingKey !== null}
              onClick={(e) => {
                e.preventDefault();
                if (confirmDelete) void handleDelete(confirmDelete);
              }}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deletingKey !== null ? (
                <span className="inline-flex items-center gap-2">
                  <Loader2 className="h-4 w-4 animate-spin" /> A apagar…
                </span>
              ) : (
                "Apagar"
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </main>
  );
}
